import React, { useEffect, useRef, useState } from "react";
import PageGuide from "../../components/PageGuide";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import ApiCall from "../../utils/apicall";
import { todayLocalISO } from "../../utils/dateFmt";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import {
  Card,
  Stack,
  Paper,
  Typography,
  Button,
  Breadcrumbs,
  Container,
  TableContainer,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Badge,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import aiscShapes from "../../data/aiscShapes.json";
import { HomeRounded } from "@mui/icons-material";
import Iconify from "../../components/iconify";
import Label from "../../components/label";

import SmartTable from "../../components/SmartTable/SmartTable";
const STATUS_COLORS = {
  quarantine: "error",
  released: "success",
  consumed: "info",
};

function HeatManagement() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { loginUser } = useSelector((state) => state.userSlice);
  const { permissions: userPermissions } = loginUser?.decodedToken ?? {}; // review-fix F-06

  const canManageHeats = hasPermission(userPermissions, PERMISSIONS.VIEW_TASK);

  // State
  const [job, setJob] = useState(null);
  const [heats, setHeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [openReceiveModal, setOpenReceiveModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [mtcFileInputRef] = useState(useRef(null));
  const [submitLoading, setSubmitLoading] = useState(false);

  const [formData, setFormData] = useState({
    heatNumber: "",
    shape: "",
    grade: "",
    supplier: "",
    quantity: "",
    unit: "lbs",
    receivedDate: todayLocalISO(),
    notes: "",
    mtcFile: null,
  });

  // Fetch job and heats
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const jobRes = await ApiCall.get(`/job/${jobId}`);
        setJob(jobRes.data?.data?.job);

        const heatsRes = await ApiCall.get(`/heat/job/${jobId}`);
        setHeats(heatsRes.data?.data?.heats || []);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load heats");
      } finally {
        setLoading(false);
      }
    };

    if (jobId) fetchData();
  }, [jobId]);

  // Filter heats
  const filteredHeats = heats.filter((heat) => {
    if (activeFilter === "All") return true;
    return heat.status?.toLowerCase() === activeFilter.toLowerCase();
  });

  // Calculate stats
  const stats = {
    total: heats.length,
    quarantine: heats.filter((h) => h.status === "quarantine").length,
    released: heats.filter((h) => h.status === "released").length,
    consumed: heats.filter((h) => h.status === "consumed").length,
  };

  // Handle form input
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMtcFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        mtcFile: file,
      }));
    }
  };

  // Submit receive heat form
  const handleSubmitReceiveHeat = async () => {
    if (!formData.heatNumber.trim()) {
      setError("Heat number is required");
      return;
    }

    setSubmitLoading(true);
    try {
      const fd = new FormData();
      fd.append("heatNumber", formData.heatNumber);
      fd.append("shape", formData.shape);
      fd.append("grade", formData.grade);
      fd.append("supplier", formData.supplier);
      fd.append("quantity", formData.quantity);
      fd.append("unit", formData.unit);
      fd.append("receivedDate", formData.receivedDate);
      fd.append("notes", formData.notes);
      fd.append("jobId", jobId);
      if (formData.mtcFile) {
        fd.append("mtcFile", formData.mtcFile);
      }

      await ApiCall.post("/heat", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Refresh heats
      const heatsRes = await ApiCall.get(`/heat/job/${jobId}`);
      setHeats(heatsRes.data?.data?.heats || []);

      setOpenReceiveModal(false);
      setFormData({
        heatNumber: "",
        shape: "",
        grade: "",
        supplier: "",
        quantity: "",
        unit: "lbs",
        receivedDate: todayLocalISO(),
        notes: "",
        mtcFile: null,
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to receive heat");
    } finally {
      setSubmitLoading(false);
    }
  };

  // Release heat
  const handleReleaseHeat = async (heatId) => {
    try {
      await ApiCall.patch(`/heat/${heatId}/release`, {
        releasedBy: loginUser?.decodedToken?.id,
      });
      const heatsRes = await ApiCall.get(`/heat/job/${jobId}`);
      setHeats(heatsRes.data?.data?.heats || []);
      setConfirmDialog(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to release heat");
    }
  };

  // Quarantine heat
  const handleQuarantineHeat = async (heatId) => {
    try {
      await ApiCall.patch(`/heat/${heatId}/quarantine`);
      const heatsRes = await ApiCall.get(`/heat/job/${jobId}`);
      setHeats(heatsRes.data?.data?.heats || []);
      setConfirmDialog(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to quarantine heat");
    }
  };

  // Delete heat
  const handleDeleteHeat = async (heatId) => {
    try {
      await ApiCall.delete(`/heat/${heatId}`);
      const heatsRes = await ApiCall.get(`/heat/job/${jobId}`);
      setHeats(heatsRes.data?.data?.heats || []);
      setConfirmDialog(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete heat");
    }
  };

  const handleViewHeat = (heatId) => {
    navigate(`/heat-detail/${heatId}`);
  };

  if (!canManageHeats) {
    return (
      <Container>
        <Typography variant="h6" color="error">
          You do not have permission to view this page.
        </Typography>
      </Container>
    );
  }

  return (
    <div>
      <Helmet>
        <title>Heat Management | SFM</title>
      </Helmet>

      <Container maxWidth="xl">
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <div>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <HomeRounded color="inherit" />
                <Typography variant="body1" color="textPrimary">
                  / Heat Management — Material Traceability
                </Typography>
              </Stack>
            </Breadcrumbs>
            <Typography variant="h4">{job?.name || "Loading..."}</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              Track heat numbers, Certified Mill Test Reports (CMTRs), and quarantine/release status for all incoming material on this job.
            </Typography>
            <PageGuide
              why="Trace every piece back to its material — heat numbers and mill test reports — so you can prove what steel went where."
              who="Receiving and QC logging heats and CMTRs; anyone proving material traceability."
              what="Heat numbers, Certified Mill Test Reports (CMTRs), and quarantine/release status for the material on this job."
              how="Receive material against a heat, attach its CMTR, then release it from quarantine once verified."
              when="When material arrives, and before any piece made from it moves forward."
              standard="AISC §2.5 material traceability"
            />
          </div>
          <Button
            variant="outlined"
            startIcon={<Iconify icon="eva:arrow-ios-back-outline" />}
            onClick={() => navigate(`/detail-Job/${jobId}`)}
          >
            Back
          </Button>
        </Stack>

        {error && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: "error.light" }}>
            <Typography color="error">{error}</Typography>
          </Paper>
        )}

        {/* Stats Bar */}
        <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: "wrap" }}>
          <Card sx={{ p: 2, flexGrow: 1, minWidth: "150px" }}>
            <Typography variant="body2" color="textSecondary">
              Total Heats Received
            </Typography>
            <Typography variant="h5">{stats.total}</Typography>
          </Card>
          <Card sx={{ p: 2, flexGrow: 1, minWidth: "150px" }}>
            <Typography variant="body2" color="textSecondary">
              In Quarantine
            </Typography>
            <Typography variant="caption" color="textSecondary">Pending QC release</Typography>
            <Typography variant="h5">
              <Badge badgeContent={stats.quarantine} color="error">
                <Iconify icon="eva:alert-outline" />
              </Badge>
            </Typography>
          </Card>
          <Card sx={{ p: 2, flexGrow: 1, minWidth: "150px" }}>
            <Typography variant="body2" color="textSecondary">
              Released to Fab
            </Typography>
            <Typography variant="caption" color="textSecondary">CMTR verified, cleared for use</Typography>
            <Typography variant="h5">
              <Badge badgeContent={stats.released} color="success">
                <Iconify icon="eva:checkmark-outline" />
              </Badge>
            </Typography>
          </Card>
          <Card sx={{ p: 2, flexGrow: 1, minWidth: "150px" }}>
            <Typography variant="body2" color="textSecondary">
              Consumed
            </Typography>
            <Typography variant="caption" color="textSecondary">Linked to fabricated pieces</Typography>
            <Typography variant="h5">
              <Badge badgeContent={stats.consumed} color="info">
                <Iconify icon="eva:archive-outline" />
              </Badge>
            </Typography>
          </Card>
        </Stack>

        {/* Filter Tabs */}
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          {["All", "Quarantine", "Released", "Consumed"].map((filter) => (
            <Button
              key={filter}
              variant={activeFilter === filter ? "contained" : "outlined"}
              onClick={() => setActiveFilter(filter)}
            >
              {filter}
            </Button>
          ))}
        </Stack>

        {/* Table */}
        <Card>
          {loading ? (
            <Typography sx={{ p: 3 }}>Loading heats...</Typography>
          ) : filteredHeats.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                {activeFilter === "All"
                  ? "No heats received yet for this job"
                  : `No ${activeFilter.toLowerCase()} heats`}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {activeFilter === "All"
                  ? "When material arrives, log each heat number and attach the CMTR. All incoming material starts in Quarantine and must be QC-reviewed before release to fabrication."
                  : `Use the filter tabs above to view heats in other statuses.`}
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <SmartTable tableId="heats" lockedColumns={["Shape / Heat No.", "Status"]}>
                <TableHead>
                  <TableRow>
                    <TableCell>Shape / Heat No.</TableCell>
                    <TableCell>Grade</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Received</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredHeats.map((heat) => (
                    <TableRow key={heat.id} hover>
                      <TableCell>
                        {heat.shape && (
                          <Typography variant="body2" fontWeight={600}>
                            {heat.shape}
                          </Typography>
                        )}
                        <Typography variant="caption" color="textSecondary">
                          Ht# {heat.heatNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>{heat.grade || "-"}</TableCell>
                      <TableCell>{heat.supplier || "-"}</TableCell>
                      <TableCell align="right">
                        {heat.quantity} {heat.unit}
                      </TableCell>
                      <TableCell>
                        <Label color={STATUS_COLORS[heat.status] || "default"}>
                          {heat.status || "unknown"}
                        </Label>
                      </TableCell>
                      <TableCell>
                        {heat.receivedDate
                          ? new Date(heat.receivedDate).toLocaleDateString()
                          : "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ display: "flex", gap: 1 }}>
                        {heat.status === "quarantine" && (
                          <MenuItem
                            onClick={() =>
                              setConfirmDialog({
                                title: "Release Heat to Fabrication?",
                                message: `You are releasing Heat ${heat.heatNumber} from quarantine. This confirms the CMTR has been reviewed and the material meets spec. Fabricators will be authorized to consume this heat.`,
                                onConfirm: () => handleReleaseHeat(heat.id),
                              })
                            }
                          >
                            <Iconify icon="eva:checkmark-outline" />
                          </MenuItem>
                        )}
                        {heat.status === "released" && (
                          <MenuItem
                            onClick={() =>
                              setConfirmDialog({
                                title: "Re-Quarantine Heat?",
                                message: `You are placing Heat ${heat.heatNumber} back in quarantine. Fabricators will no longer be authorized to use this material until it is re-released by QC.`,
                                onConfirm: () => handleQuarantineHeat(heat.id),
                              })
                            }
                          >
                            <Iconify icon="eva:alert-outline" />
                          </MenuItem>
                        )}
                        <MenuItem onClick={() => handleViewHeat(heat.id)}>
                          <Iconify icon="eva:eye-outline" />
                        </MenuItem>
                        <MenuItem
                          sx={{ color: "error.main" }}
                          onClick={() =>
                            setConfirmDialog({
                              title: "Delete Heat?",
                              message: `Delete heat ${heat.heatNumber}? This cannot be undone.`,
                              onConfirm: () => handleDeleteHeat(heat.id),
                            })
                          }
                        >
                          <Iconify icon="eva:trash-2-outline" />
                        </MenuItem>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </SmartTable>
            </TableContainer>
          )}
        </Card>

        {/* FAB Button */}
        <Button
          variant="contained"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={() => setOpenReceiveModal(true)}
          sx={{
            position: "fixed",
            bottom: 32,
            right: 32,
            borderRadius: "50%",
            width: 56,
            height: 56,
            padding: 0,
          }}
        >
          +
        </Button>
      </Container>

      {/* Receive Heat Modal */}
      <Dialog
        open={openReceiveModal}
        onClose={() => setOpenReceiveModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Receive Heat — Log Incoming Material</DialogTitle>
        <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Enter the heat number exactly as it appears on the Certified Mill Test Report (CMTR). All material starts in <strong>Quarantine</strong> until QC reviews and releases it to fabrication.
          </Typography>
          <Autocomplete
            freeSolo
            options={aiscShapes}
            groupBy={(option) => option.type}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            value={formData.shape || null}
            onInputChange={(_, newValue) =>
              setFormData((prev) => ({ ...prev, shape: newValue }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Shape / Profile (AISC Nomenclature)"
                placeholder="e.g. W14X48, HSS4X4X1/4, L4X4X1/4, PL3/4X8"
                helperText="Select from AISC 15th Ed. database or type a plate (PL) or custom shape"
                fullWidth
              />
            )}
          />
          <TextField
            label="Heat Number * (from CMTR)"
            name="heatNumber"
            value={formData.heatNumber}
            onChange={handleFormChange}
            fullWidth
            required
          />
          <TextField
            label="Grade (e.g. A572 Gr50, A36, A992)"
            name="grade"
            value={formData.grade}
            onChange={handleFormChange}
            fullWidth
          />
          <TextField
            label="Supplier"
            name="supplier"
            value={formData.supplier}
            onChange={handleFormChange}
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Quantity"
              name="quantity"
              value={formData.quantity}
              onChange={handleFormChange}
              type="number"
              fullWidth
            />
            <TextField
              select
              label="Unit"
              name="unit"
              value={formData.unit}
              onChange={handleFormChange}
              sx={{ minWidth: 100 }}
            >
              <MenuItem value="lbs">lbs</MenuItem>
              <MenuItem value="ft">ft</MenuItem>
            </TextField>
          </Stack>
          <TextField
            label="Received Date"
            name="receivedDate"
            type="date"
            value={formData.receivedDate}
            onChange={handleFormChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleFormChange}
            multiline
            rows={3}
            fullWidth
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Certified Mill Test Report (CMTR) <Typography component="span" variant="caption" color="textSecondary">— required for heat release</Typography>
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 1 }}>
              Upload the CMTR from the supplier. QC will verify this document before authorizing the heat for fabrication.
            </Typography>
            <input
              type="file"
              ref={mtcFileInputRef}
              onChange={handleMtcFileChange}
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
            />
            <Button
              variant="outlined"
              onClick={() => mtcFileInputRef.current?.click()}
              fullWidth
            >
              {formData.mtcFile ? formData.mtcFile.name : "Attach MTC (PDF or image)"}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReceiveModal(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitReceiveHeat}
            variant="contained"
            disabled={submitLoading}
          >
            {submitLoading ? "Saving..." : "Receive Heat"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <Dialog
          open={!!confirmDialog}
          onClose={() => setConfirmDialog(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <Typography>{confirmDialog.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog(null)}>Cancel</Button>
            <Button
              onClick={() => {
                confirmDialog.onConfirm();
              }}
              variant="contained"
              color="primary"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}

export default HeatManagement;
